/**
 * ChatMessagesList — the scrollable messages region of `ChatPage`. Wraps a
 * `VirtualList<ChatMessageEntity>` in `reversed` mode and dispatches each
 * item through `MessageBubble`.
 *
 * Flutter source of truth: `_messagesListWidget` + `messageItemBuilder`
 * (`chat_screen.dart:3854-3884`). The Flutter `ListView.separated(reverse:
 * true)` puts the newest message at the bottom of the visible viewport;
 * `VirtualList(reversed)` produces the same anchoring (see its JSDoc).
 *
 * ----------------------------------------------------------------------
 * Index-space note (load-bearing):
 *
 * `useChat` returns `messages` **newest-first** (lower index = newer; sorted
 * `sortByTimestampDesc`). Flutter's `isLastMessageRight` /
 * `isLastMessageLeft` corner-radii helpers are written against that same
 * coord system (`index === 0` means the most recent message, the previous
 * item at `index - 1` is later in real time). The same is true of
 * `myLastMessageReadPosition` — it's an index in the newest-first space.
 *
 * `VirtualList(reversed)`, on the other hand, expects items in **chrono-
 * logical** order (oldest at array index 0, newest at the end) — items
 * render top-to-bottom in the DOM and the viewport is anchored to the
 * bottom (see `VirtualList.tsx`'s `reversed` JSDoc).
 *
 * `ChatMessagesList` therefore feeds `VirtualList` the array in
 * chronological order (`[...messages].reverse()`) and converts each
 * `renderItem` index back to the newest-first space before computing the
 * per-message primitives that `MessageBubble` consumes:
 *
 *   newestFirstIndex = messages.length - 1 - chronologicalIndex
 *
 * Per-message primitives (Task 25 refactor): the list precomputes
 * `isLastOfRun` (via `ChatUtils.isLastMessageRight` / `isLastMessageLeft`)
 * and `readReceiptMode` (via `ChatUtils.getReadReceiptMode`) for each
 * message and threads them down as primitives. The dispatcher and the
 * subcomponents NEVER receive the whole `messages` array or this
 * message's `index` — leaks of "look at neighbors" data into the leaves are
 * a well-known cause of needless re-renders and were called out in the
 * user-review fix plan (Task 25). The list itself owns the neighbor walk
 * because it already iterates `messages` once for the render.
 * ----------------------------------------------------------------------
 *
 * The "user is at the bottom" signal (Flutter's `_currentScroll <= 0`
 * branch — `chat_screen.dart:340-356, 547-579`) is forwarded directly from
 * `VirtualList`'s `onScrolledToBottom` / `onScrolledAwayFromBottom` props
 * (Task 21 refactor). Previously this list mounted its own
 * `IntersectionObserver` on a zero-height bottom sentinel inside the
 * scroll container — duplicating the scroll-tracking machinery
 * `VirtualList` already runs internally.
 *
 * `ChatMessagesList` exposes a `ChatMessagesListHandle` via `forwardRef`
 * with a single imperative method, `scrollToBottom`. The parent
 * (`ChatMessagesRegion`) drives this handle from the unread-banner /
 * `ScrolledUpButton` tap so the viewport snaps to the bottom regardless of
 * the anchor effect's "leave alone" decision — Flutter parity at
 * `_scrollDown` (`chat_screen.dart:3414`, `:3452`). The handle delegates to
 * `VirtualList.scrollToBottom`; no DOM poking happens at this layer.
 *
 * Bubble subcomponent registrations (Tasks 24-30) are pulled in via the
 * `./bubbles` barrel so their `registerBubble(...)` side-effects fire before
 * the dispatcher is ever rendered.
 */

import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList';
import type { VirtualListHandle } from '@/presentation/components/common/virtualList';
import { ChatUtils } from '@/domain/utils/chatUtils';
import type { QaAttrOptions } from '@/presentation/utils/qaAttr';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { MessageBubble } from './bubbles';

/**
 * Imperative handle exposed by `ChatMessagesList`. Forwards `scrollToBottom`
 * to the underlying `VirtualList(reversed)` so callers (the unread banner /
 * scroll-to-bottom button, post-send auto-scroll) can drive the viewport
 * without poking the DOM directly. Mirrors Flutter's `_scrollDown`
 * (`chat_screen.dart:3414`, `:3452`).
 */
export interface ChatMessagesListHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

export interface ChatMessagesListProps {
  /**
   * Optional `data-qa-*` set spread onto the underlying `VirtualList` root so
   * the qa-tester can scope to the list. Bubble rows carry their own ids.
   */
  qa?: QaAttrOptions;
  /** Newest-first message list from `useChat` (lower index = newer). */
  messages: ChatMessageEntity[];
  /** Resolved peer user; null until the initial fetch completes. */
  peerUser: UserEntity | null;
  /** Current user's Firebase id — used by `MessageBubble` for `isOwn`. */
  currentUserId: string;
  /** Map keyed by `message.timestamp` — true means the timestamp adornment is open. */
  openTimestampForMessageId: Record<number, boolean>;
  /** Hook callback — toggle the timestamp adornment for a message. */
  onToggleTimestamp: (message: ChatMessageEntity) => void;
  /** Hook callback — retry a failed send (own text bubbles only). */
  onRetry: (message: ChatMessageEntity) => void;
  /**
   * Page callback — join an in-progress video call as the audience. Fired
   * only by LIVE peer `videoCall` bubbles (Flutter
   * `chat_screen.dart:2899-2924`). Forwarded straight to `MessageBubble`.
   */
  onVideoCallJoin?: (message: ChatMessageEntity) => void;
  /** Hook callback — top sentinel intersected, load older page. */
  onLoadMore: () => void;
  /** Hook callback — user scrolled to the bottom (newest visible). */
  onScrolledToBottom: () => void;
  /** Hook callback — user scrolled away from the bottom. */
  onScrolledAway: () => void;
  /**
   * Memoized index (in the newest-first `messages` array) of the most
   * recent own message marked `seenByPeer`, or -1 if none. Sourced from
   * `useChat.myLastMessageReadPosition` — consumed locally by
   * `ChatUtils.getReadReceiptMode` to derive each own bubble's
   * `readReceiptMode` (precomputed once per render and threaded down as a
   * primitive). NOT forwarded to `MessageBubble`.
   */
  myLastReadPosition: number;
  /** Screen key of the hosting page — forwarded to bubble subcomponents. */
  screenKey: string;
  /**
   * Sticky chrome (the page's app bar / no-internet banner above, the composer
   * below) that overlaps the *window* viewport in document flow. Forwarded
   * straight to `VirtualList`'s `reversedBottomPinInset` so the short-list
   * bottom-pin pads to `innerHeight − top − bottom` instead of the whole
   * viewport. No-op when the resolved scroll element is not `window` (see the
   * VirtualList(reversed) JSDoc). The list now flows in the document and
   * resolves `window` as its scroll element, so these insets matter.
   */
  reversedBottomPinInset?: { top?: number; bottom?: number };
}

export const ChatMessagesList = forwardRef<ChatMessagesListHandle, ChatMessagesListProps>(({
  qa,
  messages,
  peerUser,
  currentUserId,
  openTimestampForMessageId,
  onToggleTimestamp,
  onRetry,
  onVideoCallJoin,
  onLoadMore,
  onScrolledToBottom,
  onScrolledAway,
  myLastReadPosition,
  screenKey,
  reversedBottomPinInset,
}, ref) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Imperative handle to the underlying `VirtualList`. Held internally so the
  // forwarded handle can route `scrollToBottom` through without leaking the
  // VirtualList ref upward.
  const virtualListRef = useRef<VirtualListHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: (behavior: ScrollBehavior = 'smooth') => {
        virtualListRef.current?.scrollToBottom(behavior);
      },
    }),
    [],
  );

  // Chronological order for `VirtualList(reversed)` (oldest at index 0,
  // newest at the end). See the index-space note in the file header.
  // Memoized by `messages` identity so a render that touches no other
  // dependency doesn't rebuild the reversed array.
  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  // Stable key extractor. `documentId` is a UUID v1 minted by either the
  // optimistic insert (`useChat.sendText`) or the Firestore document
  // identity (`useChatLiveListener.handleAdded`) — guaranteed unique across
  // both paths.
  const getItemKey = useCallback(
    (m: ChatMessageEntity) => m.documentId,
    [],
  );

  // Render a single message. `chronologicalIndex` is the index in
  // `chronologicalMessages`; we convert back to the newest-first coord
  // system to compute the per-message primitives (`isLastOfRun`,
  // `readReceiptMode`) before handing the message to `MessageBubble`. The
  // dispatcher itself never sees `index` or `messages` (see the
  // neighbor-aware data note in the file header).
  const renderItem = useCallback(
    (message: ChatMessageEntity, chronologicalIndex: number) => {
      const newestFirstIndex = messages.length - 1 - chronologicalIndex;
      const isOpen = openTimestampForMessageId[message.timestamp] === true;
      const isOwn = message.idFrom === currentUserId;
      // Walk the neighbor ONCE here (the list already iterates `messages`
      // for the render) and pass the result as a primitive — leaves no longer
      // depend on the whole array, so React can short-circuit per-message
      // renders.
      const isLastOfRun = isOwn
        ? ChatUtils.isLastMessageRight(newestFirstIndex, messages, currentUserId)
        : ChatUtils.isLastMessageLeft(newestFirstIndex, messages, currentUserId);
      // Precomputed read-receipt mode replaces the previous
      // `index + myLastReadPosition` pair forwarded into the dispatcher.
      // The result is meaningful only for own bubbles, but we compute it
      // unconditionally so the prop shape stays uniform (peer bubbles ignore
      // it).
      const readReceiptMode = ChatUtils.getReadReceiptMode(
        message,
        newestFirstIndex,
        myLastReadPosition,
      );
      return (
        <MessageBubble
          message={message}
          peerUser={peerUser}
          currentUserId={currentUserId}
          isLastOfRun={isLastOfRun}
          readReceiptMode={readReceiptMode}
          isTimestampOpen={isOpen}
          onToggleTimestamp={(timestamp) => {
            // The dispatcher contract expects a `(timestamp) => void`; the
            // hook ergonomically expects the whole message so it can drive
            // the "only one open at a time" reset without a second lookup.
            // We bridge here — the bubble subcomponents all call
            // `onToggleTimestamp(message.timestamp)`, so on the hook side we
            // find the message by timestamp and forward it.
            const found = messages.find((m) => m.timestamp === timestamp);
            if (found !== undefined) onToggleTimestamp(found);
          }}
          onRetry={onRetry}
          onVideoCallJoin={onVideoCallJoin}
          screenKey={screenKey}
        />
      );
    },
    [
      messages,
      peerUser,
      currentUserId,
      openTimestampForMessageId,
      onToggleTimestamp,
      onRetry,
      onVideoCallJoin,
      myLastReadPosition,
      screenKey,
    ],
  );

  return (
    // Flow wrapper — NO local scroll container. The list flows in the document
    // so `VirtualList` resolves `window` as its scroll element, giving the
    // page-right document scrollbar (review obs. 1) and handing scroll-position
    // restoration to `RouteStack`'s `window.scrollY` source of truth. Horizontal
    // padding only — vertical padding on a `VirtualList(reversed)` scroll region
    // couples the bottom-pin math to computed style (see the VirtualList(reversed)
    // JSDoc "Scroll-region padding caveat"); horizontal is explicitly fine.
    // Per-bubble vertical breathing room can be applied inside `renderItem` if
    // it ever becomes needed.
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        backgroundColor: theme.background,
      }}
    >
      <VirtualList<ChatMessageEntity>
        ref={virtualListRef}
        qa={qa}
        items={chronologicalMessages}
        renderItem={renderItem}
        getItemKey={getItemKey}
        reversed
        // Sticky app bar / banner above + composer below overlap the window
        // viewport in document flow; exclude them from the short-list bottom-pin
        // so a few messages pin just above the composer with no blank gap
        // (Finding 1). No-op for window-anchored / non-window callers.
        reversedBottomPinInset={reversedBottomPinInset}
        gap={responsive.spacing.sm}
        defaultItemHeight={responsive.chat.defaultItemHeight}
        onReachedEnd={onLoadMore}
        onScrolledToBottom={onScrolledToBottom}
        onScrolledAwayFromBottom={onScrolledAway}
        // Treat lingering near the bottom as still-at-bottom so peer arrivals
        // keep auto-scrolling (and the unread banner only triggers once the
        // user has scrolled meaningfully up). See `responsive.chat.scrolledAwayThreshold`.
        scrolledAwayFromBottomThreshold={responsive.chat.scrolledAwayThreshold}
      />
    </div>
  );
});

ChatMessagesList.displayName = 'ChatMessagesList';
